import type { QueryClient } from '@tanstack/react-query';
import { TaskCategoryTaskType } from '@urbanx/agx.activities.client';
import type {
  ContractStageUpdatedEvent,
  ContractUpdatedEvent,
  TaskCollectionChangedNotification,
  TaskUpdatedNotification,
} from 'types/pushNotifications';
import { emitTaskCollectionRefresh } from 'hooks/taskCollectionRefresh';
import { isDepositWorkflowTask } from 'utils/tasks/taskWorkflow';

const autopilotTasksQueryRoot = ['getAutopilotTasksForCampaign'] as const;
const contractDetailOperations = [
  'staffGetContractDates',
  'staffGetContractDeposits',
  'staffGetContractSettlement',
  'staffGetBuyers',
  'staffGetBuyersSolicitor',
  'staffGetBuyersAgent',
];
const staffContractOperations = [
  'staffGetContract',
  ...contractDetailOperations,
  'staffGetSpecialConditions',
  'staffGetContractsForCampaign',
];
const contractDateTaskTypes = new Set([
  TaskCategoryTaskType.CoolingOffPeriod,
  TaskCategoryTaskType.FinanceDue,
  TaskCategoryTaskType.BuildingAndPestDue,
  TaskCategoryTaskType.BodyCorporateDue,
  TaskCategoryTaskType.SpecialCondition,
  TaskCategoryTaskType.Unconditional,
  TaskCategoryTaskType.Settlement,
]);

export const invalidateTaskCollection = (
  queryClient: QueryClient,
  event: TaskCollectionChangedNotification
) =>
  queryClient.invalidateQueries({
    queryKey: [...autopilotTasksQueryRoot, event.campaignId, event.contractId],
    exact: true,
  });

type TaskCollectionRefreshEmitter = (event: TaskCollectionChangedNotification) => void;

export const refreshTaskCollection = async (
  queryClient: QueryClient,
  event: TaskCollectionChangedNotification,
  emitRefresh: TaskCollectionRefreshEmitter = emitTaskCollectionRefresh
) => {
  await invalidateTaskCollection(queryClient, event);
  emitRefresh(event);
};

export const invalidateActiveTaskQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({
    queryKey: autopilotTasksQueryRoot,
    type: 'active',
    refetchType: 'active',
  });

export const refreshActiveTaskCollections = async (
  queryClient: QueryClient,
  emitRefresh: TaskCollectionRefreshEmitter = emitTaskCollectionRefresh
) => {
  const activeTaskCollections = queryClient
    .getQueryCache()
    .findAll({
      queryKey: autopilotTasksQueryRoot,
      type: 'active',
    })
    .map(({ queryKey }) => {
      const [, campaignId, contractId] = queryKey;

      if (
        typeof campaignId !== 'string' ||
        (contractId !== undefined && typeof contractId !== 'string')
      ) {
        throw new Error('Unexpected getAutopilotTasksForCampaign query key');
      }

      return { campaignId, contractId };
    });

  await invalidateActiveTaskQueries(queryClient);
  activeTaskCollections.forEach(emitRefresh);
};

export const invalidateCampaignQueriesExceptTasks = (
  queryClient: QueryClient,
  campaignId: string
) =>
  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] !== autopilotTasksQueryRoot[0] &&
      !staffContractOperations.some((operation) => operation === queryKey[0]) &&
      queryKey.includes(campaignId),
  });

export const invalidateActiveStaffContractQueries = (queryClient: QueryClient) =>
  Promise.all(
    staffContractOperations.map((operation) =>
      queryClient.invalidateQueries({
        queryKey: [operation],
        type: 'active',
        refetchType: 'active',
      })
    )
  );

// Campaign-only notifications cannot identify which contract projection changed.
export const invalidateContractDetailQueries = (
  queryClient: QueryClient,
  contract?: Pick<ContractUpdatedEvent, 'campaignId' | 'contractId'>
) =>
  Promise.all([
    ...contractDetailOperations.map((operation) =>
      queryClient.invalidateQueries({
        queryKey: contract ? [operation, contract.contractId] : [operation],
        exact: !!contract,
      })
    ),
    queryClient.invalidateQueries({
      queryKey: contract
        ? ['staffGetSpecialConditions', contract.campaignId, contract.contractId]
        : ['staffGetSpecialConditions'],
      exact: !!contract,
    }),
  ]);

export const invalidateContractDateQueries = (
  queryClient: QueryClient,
  event: TaskUpdatedNotification
) => {
  const { campaignId, contractId, category } = event.task;
  const taskType = category?.taskType;
  if (
    !campaignId ||
    !contractId ||
    (!isDepositWorkflowTask(event.task) && (!taskType || !contractDateTaskTypes.has(taskType)))
  )
    return;

  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: ['staffGetContractDates', contractId],
      exact: true,
    }),
    queryClient.invalidateQueries({
      queryKey: ['staffGetSpecialConditions', campaignId, contractId],
      exact: true,
    }),
  ]);
};

type ContractInvalidator = (contractId: string, campaignId: string) => Promise<unknown> | unknown;
type CampaignInvalidator = (campaignId: string) => Promise<unknown> | unknown;

export const refreshContractStage = (
  event: ContractStageUpdatedEvent,
  invalidateContract: ContractInvalidator,
  invalidateCampaign: CampaignInvalidator
) => {
  if (!event.campaignId || !event.contractId) return;

  return Promise.all([
    invalidateContract(event.contractId, event.campaignId),
    invalidateCampaign(event.campaignId),
  ]);
};
